'use client';
import { useState, type FormEvent } from 'react';

type Status = 'idle' | 'sending' | 'sent' | 'error';

const inputClass =
  'w-full px-4 py-3 bg-black/40 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:border-white/50 focus:ring-0 transition-colors';
const labelClass = 'block text-sm font-medium text-white/80 mb-2';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form));

    setStatus('sending');
    setError('');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Something went wrong. Please try again.');
      form.reset();
      setStatus('sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setStatus('error');
    }
  }

  if (status === 'sent') {
    return (
      <div className="p-8 bg-black/50 backdrop-blur-sm border border-white/15 rounded-2xl text-center space-y-4">
        <h3 className="text-2xl font-bold text-white">Thanks for reaching out!</h3>
        <p className="text-muted-foreground">
          Your message has been sent. I&apos;ll get back to you as soon as possible.
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="text-sm text-white underline underline-offset-4 hover:text-white/80"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="p-6 md:p-8 bg-black/50 backdrop-blur-sm border border-white/15 rounded-2xl space-y-5"
    >
      {/* Honeypot field for bots */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        aria-hidden="true"
      />

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="contact-name" className={labelClass}>
            Name <span className="text-white/50">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            maxLength={200}
            className={inputClass}
            placeholder="Your name"
          />
        </div>
        <div>
          <label htmlFor="contact-business" className={labelClass}>
            Business
          </label>
          <input
            id="contact-business"
            name="business"
            type="text"
            maxLength={200}
            className={inputClass}
            placeholder="Optional"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className={labelClass}>
            Email <span className="text-white/50">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            maxLength={320}
            className={inputClass}
            placeholder="you@example.com"
          />
        </div>
        <div>
          <label htmlFor="contact-phone" className={labelClass}>
            Phone
          </label>
          <input
            id="contact-phone"
            name="phone"
            type="tel"
            maxLength={50}
            className={inputClass}
            placeholder="Optional"
          />
        </div>
      </div>

      <div>
        <label htmlFor="contact-description" className={labelClass}>
          Description of work <span className="text-white/50">*</span>
        </label>
        <textarea
          id="contact-description"
          name="description"
          required
          rows={5}
          maxLength={5000}
          className={`${inputClass} resize-y`}
          placeholder="Tell me about your project, date, location, and what you have in mind..."
        />
      </div>

      {status === 'error' && <p className="text-sm text-red-400">{error}</p>}

      <button
        type="submit"
        disabled={status === 'sending'}
        className="w-full px-8 py-4 bg-white text-background rounded-lg font-medium hover:bg-white/90 transition-all duration-300 hover:-translate-y-1 disabled:opacity-60 disabled:hover:translate-y-0"
      >
        {status === 'sending' ? 'Sending...' : 'Send Message'}
      </button>
    </form>
  );
}
